'use client';

import { cn } from '@/lib/utils';
import { AvatarWithFlair, LEVEL_LABELS } from '@/components/prelaunch/avatar-with-flair';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import { GamePanel } from '../ui/GamePanel';
import { LiveDot } from '../ui/LiveDot';
import type { MockBoardHolder } from '../data/mock-boards';

interface HoldersPanelProps {
  holders: MockBoardHolder[];
  /** Live presence count when available; `null` hides the numeric (e.g. guest). */
  chatOnline: number | null;
  /** Driven by `isBoardDataLoading` in `LobbyClient` — renders six skeleton
   *  rows in place of the holder list so a live board never flashes the
   *  previous board's top holders while the new grid query is in flight. */
  loading?: boolean;
  /** Connected wallet address. When a holder row matches (case-insensitive)
   *  we replace its display name with "you" so viewers can spot themselves
   *  without scanning truncated 0x… strings. `null` for guests. */
  viewerAddress?: string | null;
  className?: string;
}

export function HoldersPanel({
  holders,
  chatOnline,
  loading = false,
  viewerAddress = null,
  className,
}: HoldersPanelProps) {
  const profileModal = useProfileModalOptional();
  const viewerAddressLc = viewerAddress?.toLowerCase() ?? null;
  const presenceLabel =
    chatOnline === null ? '—' : chatOnline > 0 ? String(chatOnline) : '—';
  const presenceMuted = chatOnline === null || chatOnline <= 0;

  return (
    <GamePanel
      title="Top Holders"
      className={cn('w-full', className)}
      bodyClassName="p-0"
      trailing={
        <span
          className="flex items-center gap-1"
          title={
            chatOnline === null
              ? 'Connect wallet for chat presence'
              : 'Chat presence (best-effort)'
          }
        >
          <LiveDot color={presenceMuted ? 'gold' : 'green'} size={5} />
          <span
            className={cn(
              'text-[9px] font-mono tracking-[0.2em] uppercase',
              presenceMuted ? 'text-zinc-500' : 'text-emerald-300/80',
            )}
          >
            {presenceLabel}
          </span>
        </span>
      }
    >
      {loading ? (
        <div className="px-2 pt-2 pb-2 space-y-1" aria-busy="true" aria-live="polite">
          {Array.from({ length: 6 }).map((_, i) => (
            <HoldersSkeletonRow key={i} />
          ))}
        </div>
      ) : (
        <>
          {holders.length === 0 && (
            <div className="py-3 px-2 text-center text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-500">
              No holders yet
            </div>
          )}

          <div className="relative">
            <div
              data-gild-scroll
              className="max-h-[240px] overflow-y-auto px-2 pt-2 pb-2 space-y-1"
            >
              {holders.map((h, i) => {
                const isViewer =
                  viewerAddressLc !== null && h.address.toLowerCase() === viewerAddressLc;
                return (
                  <button
                    key={`${h.address}-${i}`}
                    type="button"
                    onClick={() => profileModal?.openProfile(h.address)}
                    className={cn(
                      'w-full flex items-center gap-2 p-1 rounded-[3px] transition-colors text-left',
                      i < 3 ? 'bg-amber-500/[0.04]' : '',
                      isViewer && 'bg-emerald-400/[0.07] ring-1 ring-emerald-400/30',
                      'hover:bg-white/[0.04]',
                      profileModal && 'cursor-pointer',
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center justify-center w-4 h-4 rounded-[2px] text-[9px] font-mono font-bold shrink-0',
                        i === 0
                          ? 'bg-amber-400/20 border border-amber-400/50 text-amber-100'
                          : i === 1
                            ? 'bg-slate-400/15 border border-slate-300/40 text-slate-100'
                            : i === 2
                              ? 'bg-orange-600/20 border border-orange-500/40 text-orange-100'
                              : 'bg-black/60 border border-amber-500/15 text-amber-200',
                      )}
                    >
                      {i + 1}
                    </div>
                    <AvatarWithFlair
                      avatarUrl={h.avatarUrl}
                      address={h.address}
                      level={h.level}
                      size="xs"
                    />
                    <div className="flex-1 min-w-0">
                      <div
                        className={cn(
                          'text-[11px] truncate font-medium',
                          isViewer ? 'text-emerald-200' : 'text-zinc-100',
                        )}
                      >
                        {isViewer ? 'you' : h.name}
                      </div>
                      <div className="text-[9px] font-mono tracking-[0.14em] uppercase text-zinc-500">
                        {LEVEL_LABELS[h.level] ?? 'Unranked'}
                      </div>
                    </div>
                    <div className="text-[10px] text-amber-200/80 font-mono tabular-nums shrink-0 min-w-[2ch]">
                      {h.seatCount != null ? h.seatCount : '—'}
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Scroll fade at top + bottom to suggest more content */}
            {holders.length > 5 && (
              <>
                <div
                  aria-hidden
                  className="pointer-events-none absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-black/40 to-transparent"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-t from-black/40 to-transparent"
                />
              </>
            )}
          </div>

          <div className="px-3 py-1.5 border-t border-amber-500/15 text-[9px] font-mono tracking-[0.2em] uppercase text-zinc-500 flex items-center justify-between">
            <span>{holders.length} holders</span>
            <span className="text-amber-200/60">scroll ↓</span>
          </div>
        </>
      )}
    </GamePanel>
  );
}

/** Matches the live row layout (rank chip + avatar + two-line label + count
 *  column) so the skeleton doesn't reflow when real data arrives. */
function HoldersSkeletonRow() {
  return (
    <div className="w-full flex items-center gap-2 p-1 rounded-[3px]">
      <div className="flex items-center justify-center w-4 h-4 rounded-[2px] shrink-0 bg-white/5 animate-pulse" />
      <div className="w-6 h-6 rounded-full bg-white/5 animate-pulse shrink-0" />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="h-[10px] w-3/4 rounded-[2px] bg-white/[0.07] animate-pulse" />
        <div className="h-[8px] w-1/2 rounded-[2px] bg-white/5 animate-pulse" />
      </div>
      <div className="h-[10px] w-[2ch] rounded-[2px] bg-white/5 animate-pulse shrink-0" />
    </div>
  );
}
